import { useEffect, useState } from 'react';
import { CronExpressionParser } from 'cron-parser';

/**
 * Computes the next fire time from a Spring cron expression (6 fields with seconds).
 * Returns epoch millis or null if parsing fails.
 */
export const getNextFireTime = (cron: string): number | null => {
    try {
        const interval = CronExpressionParser.parse(cron, { currentDate: new Date() });
        return interval.next().getTime();
    } catch {
        return null;
    }
};

/**
 * Returns a live countdown in seconds until the next fire of a cron expression.
 * Automatically advances to the next occurrence when the current one passes.
 */
export const useCountdown = (cron: string): number | null => {
    const [secondsLeft, setSecondsLeft] = useState<number | null>(() => {
        const next = getNextFireTime(cron);
        return next !== null ? Math.max(0, Math.floor((next - Date.now()) / 1000)) : null;
    });

    useEffect(() => {
        let nextFire = getNextFireTime(cron);
        if (nextFire === null) {
            setSecondsLeft(null);
            return;
        }

        const update = () => {
            const now = Date.now();
            if (nextFire !== null && now >= nextFire) {
                // Current fire time passed, compute the next one
                nextFire = getNextFireTime(cron);
            }
            if (nextFire === null) {
                setSecondsLeft(null);
                return;
            }
            setSecondsLeft(Math.max(0, Math.floor((nextFire - now) / 1000)));
        };

        update();
        const timer = setInterval(update, 1000);
        return () => clearInterval(timer);
    }, [cron]);

    return secondsLeft;
};

/**
 * Formats seconds into a human-readable countdown string.
 */
export const formatCountdown = (seconds: number | null): string => {
    if (seconds === null) return '—';
    if (seconds <= 0) return 'now';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
};
